import { Link } from 'react-router-dom';
import { comics, hotComics, latestUpdates, categories } from '@/mocks/comics';

function formatViews(views: number): string {
  if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return views.toString();
}

export default function HomePage() {
  const hotList = hotComics.map((id) => comics.find((c) => c.id === id)!).filter(Boolean);
  const latestList = latestUpdates.map((id) => comics.find((c) => c.id === id)!).filter(Boolean);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '基德漫画网',
    url: 'https://jidemanhua.com',
    description: '基德漫画网汇聚无删减韩国漫画，下拉式免费韩漫大全在线阅读流畅，优质韩漫作品持续上新，满足阅读需求。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://jidemanhua.com/category/all?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '基德漫画网',
    url: 'https://jidemanhua.com',
    description: '汇聚无删减韩国漫画，下拉式免费韩漫大全在线阅读流畅，优质韩漫作品持续上新。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@jidemanhua.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(orgLd)}</script>

      <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20art%20style%20illustration%20with%20abstract%20warm%20orange%20and%20coral%20gradient%20background%2C%20soft%20floating%20comic%20panel%20elements%2C%20artistic%20minimal%20composition%2C%20warm%20atmospheric%20lighting%2C%20clean%20modern%20design%2C%20high%20quality%20digital%20art%2C%20vertical%20flow%20aesthetic&width=1600&height=900&seq=hero-bg-comic&orientation=landscape"
          alt="基德漫画网 - 韩国漫画在线阅读平台"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="基德漫画网 - 韩国漫画无删减漫画下拉式免费韩漫大全"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div className="relative z-10 w-full max-w-3xl mx-auto text-center px-4">
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            基德漫画网
          </h1>
          <p className="text-base md:text-lg text-white/85 mb-3 leading-relaxed">
            汇聚<strong>无删减韩国漫画</strong>，下拉式免费韩漫大全在线阅读流畅
          </p>
          <p className="text-sm md:text-base text-white/70 mb-8">
            优质韩漫作品持续上新，满足阅读需求
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-white text-sm md:text-base font-semibold rounded-full whitespace-nowrap hover:bg-primary-600 transition-all duration-200 cursor-pointer"
            >
              <i className="ri-book-open-line text-lg"></i>
              立即开始阅读
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white/15 backdrop-blur-sm text-white text-sm md:text-base font-medium rounded-full whitespace-nowrap hover:bg-white/25 transition-all duration-200 cursor-pointer border border-white/20"
            >
              <i className="ri-information-line text-lg"></i>
              了解更多
            </Link>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background-50 to-transparent"></div>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                热门推荐
              </h2>
              <p className="text-sm text-foreground-600 mt-1">最受欢迎的韩国漫画精选</p>
            </div>
            <Link
              to="/category/all"
              className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              查看全部 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
            {hotList.map((comic) => (
              <Link
                key={comic.id}
                to={`/article/${comic.id}`}
                className="group cursor-pointer"
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-3">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩国漫画 - 基德漫画网`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500/90 text-background-50 text-xs font-semibold">
                      <i className="ri-fire-line text-xs"></i>
                      {comic.rating}
                    </span>
                  </div>
                  {comic.status === '已完结' && (
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-0.5 rounded-full bg-foreground-800/80 text-background-50 text-xs">
                        {comic.status}
                      </span>
                    </div>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                  {comic.title}
                </h3>
                <p className="text-xs text-foreground-500 mt-1">{formatViews(comic.views)} 阅读</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                最新更新
              </h2>
              <p className="text-sm text-foreground-600 mt-1">每日持续更新优质韩漫</p>
            </div>
            <Link
              to="/category/all"
              className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              更多更新 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-product-shop="">
            {latestList.slice(0, 8).map((comic, idx) => (
              <Link
                key={comic.id}
                to={`/article/${comic.id}`}
                className="flex gap-3 bg-background-50 rounded-lg p-3 hover:bg-background-100 transition-colors cursor-pointer group"
              >
                <div className="w-16 h-20 flex-shrink-0 rounded-md overflow-hidden bg-background-100">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩国漫画 - 基德漫画网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {comic.title}
                  </h4>
                  <p className="text-xs text-foreground-500 mt-1 line-clamp-1">{comic.description}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-accent-600 font-medium">
                      {comic.chapters_list[0]?.title}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-xs text-foreground-400">{comic.author}</span>
                    <span className="text-xs text-foreground-400">共{comic.chapters}话</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              漫画分类
            </h2>
            <p className="text-sm text-foreground-600 mt-2">探索不同类型的韩国漫画</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
            {categories.filter((c) => c.slug !== 'all').map((cat) => {
              const count = comics.filter((c) => c.category === cat.slug).length;
              return (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-3 bg-background-100 hover:bg-background-200/70 rounded-lg p-4 transition-colors cursor-pointer group"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${cat.icon} text-lg text-primary-500`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors">
                      {cat.name}
                    </h4>
                    <p className="text-xs text-foreground-500">{count}部作品</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}